import type { TruncationMode } from '@dynatrace/strato-components/typography';

import { truncateText } from '../../../../core/utils/text-truncation.js';
import { LABEL_OPTIONS } from '../../../constants.js';
import type { LabelOptions } from '../../../types/canvas.js';

type LabelWidthOptions = {
  nameWidth: number;
  nameValueWidth: number;
  multiLineWidth: number;
};

export const LINE_SPACING = 2;
export const FONT_SIZE_DIFFERENCE = 1;

/**
 * Returns the labelOption that fits the current node
 * @internal
 * @param height - height of the node.
 * @param width - width of the node.
 * @param fontSize - font size of the label.
 * @param padding - padding around the label
 * @param labelWidth - object containing width for each labelFit scenario,
 */
export const getLabelFit = (
  height: number,
  width: number,
  fontSize: number,
  padding: number,
  labelWidth: LabelWidthOptions,
) => {
  const { nameWidth, nameValueWidth, multiLineWidth } = labelWidth;
  const lineSpacing = 2;
  const paddingSum = 2 * padding;
  const availableWidth = width - paddingSum;
  const minInlineHeight = paddingSum + fontSize;
  const minMultiLineHeight = paddingSum + fontSize * 2 + lineSpacing;

  const fitMultiLine =
    height >= minMultiLineHeight && availableWidth >= multiLineWidth;
  if (fitMultiLine) {
    return LABEL_OPTIONS.Multi;
  }

  const fitInline =
    height >= minInlineHeight && availableWidth >= nameValueWidth;
  if (fitInline) {
    return LABEL_OPTIONS.Inline;
  }

  const fitName = height >= minInlineHeight && availableWidth >= nameWidth;
  if (fitName) {
    return LABEL_OPTIONS.Name;
  }

  const fitEllipsis = height >= minInlineHeight && availableWidth < nameWidth;
  if (fitEllipsis) {
    return LABEL_OPTIONS.Ellipsis;
  }

  return LABEL_OPTIONS.Fallback;
};

/**
 * Returns the center of the node.
 * @internal
 * @param height - height of the node.
 * @param width - width of the node.
 */
export const getRectCenter = (width: number, height: number) => ({
  centerX: width / 2,
  centerY: height / 2,
});

/**
 * Returns the name width for the given context
 * @internal
 * @param name - current node name.
 * @param ctx - canvas context.
 */
export const getNameWidth = (name: string, ctx: CanvasRenderingContext2D) =>
  ctx.measureText(name).width;

/**
 * Returns the object containing width for each labelFit scenario
 * @internal
 * @param name - current node name.
 * @param value - current node value.
 * @param ctx - canvas context.
 */
export const getLabelWidth = (
  nameWidth: number,
  value: string,
  ctx: CanvasRenderingContext2D,
) => {
  const nameValueWidth = nameWidth + ctx.measureText(` ${value}`).width;
  const multiLineWidth = Math.max(nameWidth, ctx.measureText(value).width);

  return {
    nameWidth,
    nameValueWidth,
    multiLineWidth,
  };
};

/**
 * Returns the array containing the label items for a given node.
 * Depending on the available space labels are shown in 2 lines or in 1 line or with name only.
 * @internal
 * @param labelFit - current labelFit scenario
 * @param name - current node name.
 * @param value - current node value.
 */
export const getAppliedLabels = (
  labelFit: LabelOptions,
  name: string,
  value: string,
) => {
  const labelMap = {
    multi: [name, value],
    inline: [`${name} ${value}`],
    name: [name],
    ellipsis: [name],
    fallback: [],
  };

  return labelMap[labelFit];
};

/**
 * Returns the vertical offset of the labels.
 * @internal
 * @param labels - array of label for the given node.
 * @param labelIndex - current index of the labels array item.
 * @param fontSize - font size value.
 */
export const getOffsetY = (
  labels: string[],
  labelIndex: number,
  fontSize: number,
) => {
  const downOffsetY = Math.round(fontSize / 2) + LINE_SPACING;
  const upOffsetY = -1 * downOffsetY - FONT_SIZE_DIFFERENCE;
  const isMultiLine = labels.length > 1;
  return isMultiLine ? (labelIndex === 0 ? upOffsetY : downOffsetY) : 0;
};

export const applyEllipsis = (
  ctx: CanvasRenderingContext2D,
  str: string,
  maxWidth: number,
  truncationMode: TruncationMode,
) => {
  return truncateText(str, truncationMode, (truncatedText: string) => {
    const width = ctx.measureText(truncatedText).width;
    return width <= maxWidth;
  });
};
